import { useEffect, useRef, useState } from 'react';
import { json, LoaderFunction, MetaFunction } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { NEWS_CATEGORIES, NEWS_CATEGORY_MAPPING } from '~/constants/news';
import { motion } from 'framer-motion';
import { formatDate } from '~/lib/utils';
import { Calendar, ArrowLeft, BookOpen, Tag, Clock, User } from 'lucide-react';
import { CategoryCard, CategoryType } from '~/components/ui/category-card';
import parse from 'html-react-parser';

// 뉴스 타입 정의
interface News {
  id: string;
  title: string;
  content: string;
  summary?: string;
  thumbnail_url?: string;
  category: string;
  view_count: number;
  created_at: string;
  admin?: {
    name: string;
  };
}

// 뉴스 카테고리 매핑 확장 타입
type NewsCategory = keyof typeof NEWS_CATEGORY_MAPPING;

// 실제 DB 카테고리를 CategoryType으로 변환하는 함수
const mapDBCategoryToCategoryType = (category: string): CategoryType => {
  switch (category) {
    case 'firm_news':
      return 'news';
    case 'case_analysis':
      return 'case';
    case 'legal_trends':
      return 'trend';
    case 'legal_column':
      return 'column';
    default:
      return 'news';
  }
};

// 콘텐츠에서 첫 100자 추출
const extractContentPreview = (content: string): string => {
  // HTML 태그 제거
  const plainText = content.replace(/<[^>]+>/g, '');
  return plainText.slice(0, 100);
};

// 카테고리별 배경색 및 텍스트 색상 가져오기
function getColorForCategory(category: string): string {
  switch (category) {
    case 'news':
      return 'bg-indigo-50 text-indigo-700';
    case 'case':
      return 'bg-purple-50 text-purple-700';
    case 'trend':
      return 'bg-amber-50 text-amber-700';
    case 'column':
      return 'bg-rose-50 text-rose-700';
    default:
      return 'bg-gray-50 text-gray-700';
  }
}

// 카테고리별 테마 색상
function getThemeColorForCategory(category: string): string {
  switch (category) {
    case 'news':
      return 'text-indigo-700';
    case 'case':
      return 'text-purple-700';
    case 'trend':
      return 'text-amber-700';
    case 'column':
      return 'text-rose-700';
    default:
      return 'text-gray-700';
  }
}

// 카테고리별 배경 그라데이션
function getGradientForCategory(category: string): string {
  switch (category) {
    case 'news':
      return 'from-indigo-50/30 to-white';
    case 'case':
      return 'from-purple-50/30 to-white';
    case 'trend':
      return 'from-amber-50/30 to-white';
    case 'column':
      return 'from-rose-50/30 to-white';
    default:
      return 'from-gray-50 to-white';
  }
}

// 카드 변형 선택 - 모든 카테고리에 동일한 elegant 스타일 적용
const getCategoryCardVariant = (category: string, index: number) => {
  return 'elegant';
};

// 배열로 변환한 NEWS_CATEGORIES 생성
const NEWS_CATEGORIES_ARRAY = Object.values(NEWS_CATEGORIES);

// 사이드바 배경색 가져오기
function getSidebarBgForCategory(category: string): string {
  switch (category) {
    case 'news':
      return 'bg-gradient-to-br from-indigo-50 to-white';
    case 'case':
      return 'bg-gradient-to-br from-purple-50 to-white';
    case 'trend':
      return 'bg-gradient-to-br from-amber-50 to-white';
    case 'column':
      return 'bg-gradient-to-br from-rose-50 to-white';
    default:
      return 'bg-gradient-to-br from-gray-50 to-white';
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.news) {
    return [
      { title: '페이지를 찾을 수 없습니다 - 법무법인 휘상' },
      { name: 'description', content: '요청하신 페이지를 찾을 수 없습니다.' },
    ];
  }

  const title = `${data.news.title} - 법무법인 휘상`;
  const description = data.news.summary || `법무법인 휘상의 ${NEWS_CATEGORY_MAPPING[data.news.category]} 소식입니다.`;
  const imageUrl = data.news.thumbnail_url || 'https://jp-hwisang.co.kr/images/og-image.jpg';

  return [
    // 기본 메타 태그
    { title },
    { name: 'description', content: description },

    // Open Graph 메타 태그
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: imageUrl },
    { property: 'og:url', content: `https://jp-hwisang.co.kr/news/${data.news.id}` },
    { property: 'og:site_name', content: '법무법인 휘상' },

    // 트위터 카드 메타 태그
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },

    // 네이버 검색엔진 최적화 태그
    { name: 'naver-site-verification', content: '944cfa005740720739e169ba6bf7a90af30ee011' },
  ];
};

// 구글 폰트 스타일 추가
export function links() {
  return [
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;600&display=swap"
    }
  ];
}

export const loader: LoaderFunction = async ({ params }) => {
  const id = params.id;
  if (!id) {
    throw new Response('Not Found', { status: 404 });
  }

  try {
    // 서버 측 모듈 동적 임포트
    const { getNewsDetail, getRelatedNews, getRecentNewsByCategory } = await import('~/actions/news/index.server');

    // 뉴스 상세 정보 조회
    const news = await getNewsDetail(id);

    if (!news) {
      throw new Response('Not Found', { status: 404 });
    }

    // 관련 뉴스 조회
    const relatedNews = await getRelatedNews({
      id,
      category: news.category,
      limit: 4
    });

    // 같은 카테고리의 최신 글 3개 조회
    const recentNews = await getRecentNewsByCategory({
      category: news.category,
      excludeId: id,
      limit: 3
    });

    return json({ news, relatedNews, recentNews });
  } catch (error) {
    console.error('뉴스 로딩 오류:', error);
    throw new Response('Not Found', { status: 404 });
  }
};

export default function NewsDetail() {
  const loaderData = useLoaderData<typeof loader>();
  const { news, relatedNews, recentNews } = loaderData;
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // 스크롤 이벤트에 따른 헤더 상태 변경
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const scrollPosition = window.scrollY;
        const headerHeight = headerRef.current.offsetHeight;
        if (scrollPosition > headerHeight / 2) {
          setIsHeaderCollapsed(true);
        } else {
          setIsHeaderCollapsed(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 이미지에 클래스 추가하기 위한 효과
  useEffect(() => {
    if (contentRef.current) {
      const images = contentRef.current.querySelectorAll('img');
      images.forEach((img) => {
        img.classList.add('rounded-sm', 'my-8', 'mx-auto', 'max-w-full', 'h-auto');
      });
    }
  }, []);

  // 이미지와 문단 스타일 처리를 위한 효과
  useEffect(() => {
    if (contentRef.current) {
      // 이미지 스타일 적용
      const images = contentRef.current.querySelectorAll('img');
      images.forEach((img) => {
        img.classList.add('rounded-sm', 'my-8', 'mx-auto', 'max-w-full', 'h-auto');
      });

      // 문단 내 줄바꿈 처리
      const paragraphs = contentRef.current.querySelectorAll('p');
      paragraphs.forEach((p) => {
        // 텍스트 내 줄바꿈을 <br> 태그로 변환
        if (p.innerHTML.includes('\n')) {
          p.innerHTML = p.innerHTML.replace(/\n/g, '<br>');
        }
      });
    }
  }, []);

  // JSON-LD 데이터 생성
  const newsArticleData = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": news.title,
    "description": news.summary || `법무법인 휘상의 ${NEWS_CATEGORY_MAPPING[news.category]} 소식입니다.`,
    "image": news.thumbnail_url || "https://jp-hwisang.co.kr/images/og-image.jpg",
    "datePublished": news.created_at,
    "dateModified": news.updated_at,
    "author": {
      "@type": "Person",
      "name": news.admin?.name || "법무법인 휘상"
    },
    "publisher": {
      "@type": "Organization",
      "name": "법무법인 휘상",
      "logo": {
        "@type": "ImageObject",
        "url": "https://jp-hwisang.co.kr/images/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://jp-hwisang.co.kr/news/${news.id}`
    }
  };

  // 카테고리 정보
  const categoryType = mapDBCategoryToCategoryType(news.category);
  const categoryLabel = NEWS_CATEGORY_MAPPING[news.category];
  const themeColor = getThemeColorForCategory(categoryType);
  const gradientBg = getGradientForCategory(categoryType);

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* 고정 헤더 - 스크롤 시 표시 */}
      <div
        className={`fixed top-14 left-0 right-0 z-30 bg-white border-b border-gray-200 transition-all duration-300 ${isHeaderCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-base font-light text-gray-900 truncate max-w-md font-['Noto_Sans_KR']">
              {news.title}
            </h2>
          </div>
          <Link
            to="/news"
            prefetch="intent"
            className="text-gray-500 hover:text-gray-900 flex items-center text-sm"
          >
            <ArrowLeft size={14} className="mr-1" />
            <span>목록</span>
          </Link>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className={`py-12 bg-gradient-to-b ${gradientBg}`}>
        <div className="max-w-6xl mx-auto px-4" ref={headerRef}>
          {/* 상단 내비게이션 */}
          <div className="mb-10">
            <Link
              to="/news"
              prefetch="intent"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-6"
            >
              <ArrowLeft size={16} className="mr-2" />
              <span className="font-light">뉴스 목록</span>
            </Link>

            {/* 카테고리 배지 */}
            <div className="mb-6">
              <span className={`text-xs uppercase tracking-wider ${themeColor} font-medium`}>
                {categoryLabel}
              </span>
            </div>

            {/* 제목 */}
            <h1
              className="text-3xl md:text-4xl font-light text-gray-900 mb-6 tracking-tight leading-tight font-['Noto_Sans_KR']"
            >
              {news.title}
            </h1>

            {/* 메타 정보 */}
            <div className="flex items-center text-sm text-gray-500 gap-4 md:gap-6 mb-8 border-t border-b border-gray-200 py-3">
              <span className="flex items-center">
                <Calendar size={14} className="mr-1.5" />
                {formatDate(news.created_at)}
              </span>
              {news.admin?.name && (
                <span className="flex items-center">
                  <User size={14} className="mr-1.5" />
                  <span>{news.admin.name}</span>
                </span>
              )}
            </div>
          </div>

          {/* 콘텐츠 영역 - 2열 레이아웃 */}
          <div className="flex flex-col lg:flex-row gap-10">
            {/* 메인 콘텐츠 */}
            <div className="w-full lg:w-2/3">
              <div className="bg-white border border-gray-100">
                {/* 뉴스 내용 */}
                <div
                  ref={contentRef}
                  className="py-12 prose prose-lg mx-auto max-w-[38rem]
                    font-['Noto_Sans_KR'] text-gray-900 font-light
                    prose-headings:font-['Noto_Sans_KR'] prose-headings:text-gray-900 prose-headings:tracking-tight prose-headings:font-light
                    prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
                    prose-p:text-gray-800 prose-p:leading-loose prose-p:font-['Noto_Sans_KR'] prose-p:text-base prose-p:font-light prose-p:[margin-bottom:1.5em] prose-p:max-w-[40rem] prose-p:mx-auto prose-p:whitespace-pre-line
                    prose-a:text-gray-600 prose-a:no-underline hover:prose-a:underline 
                    prose-img:my-8
                    prose-strong:text-gray-900 prose-strong:font-normal
                    prose-li:text-gray-800 prose-li:leading-relaxed prose-li:font-light
                    prose-hr:border-gray-200
                    prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-gray-700 prose-blockquote:font-light
                    prose-table:border-collapse prose-table:w-full
                    prose-th:border prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:p-2 prose-th:text-left prose-th:text-sm
                    prose-td:border prose-td:border-gray-300 prose-td:p-2 prose-td:text-sm
                    prose-pre:bg-gray-50 prose-pre:rounded-none prose-pre:p-4 prose-pre:text-sm"
                >
                  {parse(news.content.split('\n').map((line: string) =>
                    line.trim() === '' ? '<p><br></p>' : `<p>${line}</p>`
                  ).join(''))}
                </div>
              </div>
            </div>

            {/* 사이드바 - 최신 게시물 */}
            <div className="w-full lg:w-1/3 lg:sticky lg:top-20 self-start">
              <div className={`${getSidebarBgForCategory(categoryType)} border border-gray-100 rounded-lg p-6 shadow-sm`}>
                <h3 className={`text-lg font-medium ${themeColor} mb-5 pb-2 border-b border-gray-200`}>
                  {categoryLabel} 최신 소식
                </h3>

                {recentNews && recentNews.length > 0 ? (
                  <div className="space-y-6">
                    {recentNews.map((item: News) => (
                      <Link
                        key={item.id}
                        to={`/news/${item.id}`}
                        prefetch="intent"
                        className="block group"
                      >
                        <div className="flex space-x-4">
                          <div className="w-16 h-16 bg-white/70 rounded-md flex-shrink-0 overflow-hidden shadow-sm group-hover:shadow transition-shadow">
                            {item.thumbnail_url ? (
                              <img
                                src={item.thumbnail_url}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center ${themeColor} bg-white`}>
                                <BookOpen size={18} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-gray-700 transition-colors font-['Noto_Sans_KR']">
                              {item.title}
                            </h4>
                            <div className="mt-1 flex items-center text-xs text-gray-500">
                              <Clock size={12} className="mr-1" />
                              <span>{formatDate(item.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">최신 소식이 없습니다.</p>
                )}

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Link
                    to={`/news?category=${news.category}`}
                    className={`text-sm ${themeColor} hover:opacity-80 flex items-center justify-center w-full font-medium transition-opacity`}
                  >
                    <span>{categoryLabel} 더보기</span>
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 관련 뉴스 섹션 */}
      {relatedNews && relatedNews.length > 0 && (
        <div className="py-16 bg-white border-t border-gray-200">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className={`text-base ${themeColor} uppercase tracking-wider font-medium mb-8 flex items-center`}>
              <span>관련 기사</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {relatedNews.map((item: News) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  prefetch="intent"
                  className="group hover:opacity-70 transition-opacity duration-200"
                >
                  <h3 className="font-light text-gray-900 mb-2 group-hover:underline text-lg font-['Noto_Sans_KR'] leading-snug">
                    {item.title}
                  </h3>
                  <div className="flex items-center text-xs text-gray-500 gap-4">
                    <span className={getThemeColorForCategory(mapDBCategoryToCategoryType(item.category))}>
                      {NEWS_CATEGORY_MAPPING[item.category]}
                    </span>
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 구조화된 데이터 마크업 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(newsArticleData) }}
      />
    </div>
  );
} 